import { isDevAuthMock } from '@ext/auth/index';
import { getSupabase } from '@ext/config/supabase';
import { setPlatform } from '@ext/platform';
import { getSyncEngine } from '@ext/services/syncEngine';
import { setStorageBackend } from '@ext/storage/index';
import { AfterOnboarding } from './components/AfterOnboarding';
import { InstallBanner } from './components/InstallBanner';
import { ReviewInExtension } from './components/ReviewInExtension';
import { BillingFooter } from '@ext/components/billing/BillingFooter';
import { createStartOver } from './components/StartOver';
import { APP_ENV, PADDLE_CONFIG } from './env';
import { SENSES_SINCE, extensionVersion, syncNow, versionAtLeast } from './extensionBridge';
import { clearDataWhenSignedOut, clearLocalData } from './signOut';
import { createIdbStore, type IdbStore } from './storage/idbStore';

/**
 * Turns the extension's dashboard into the website's, before anything renders: data goes to
 * IndexedDB, reviews, the install banner and the end of onboarding talk to the extension, and a
 * sign-out empties the browser's copy of the data.
 *
 * To the account the website is one more device: the dashboard's own sync engine sends what the
 * learner changes here and fetches what changed elsewhere (it starts with the signed-in page,
 * in AuthGate). One thing is added: once changes made here have reached the server, an installed
 * extension is asked to fetch them at once, so its reminder and badge are right without waiting
 * for its next sync.
 */
export function setUpWebPlatform(
  store: IdbStore = createIdbStore(),
  tellExtension: () => Promise<unknown> = syncNow
): void {
  setStorageBackend(store);
  // Staging and development only: the production site has no way to wipe an account.
  const StartOver =
    APP_ENV === 'production'
      ? null
      : createStartOver({
          resetOnServer: async () => {
            // The simulated development account lives in this browser only.
            if (isDevAuthMock()) return;
            const { error } = await getSupabase().functions.invoke('reset-account', { body: {} });
            if (error) throw error;
          },
          clearLocal: () => clearLocalData(store),
          tellExtension: () => syncNow(),
          reload: () => window.location.reload(),
        });
  setPlatform({
    SettingsExtras: StartOver,
    SignInFooter: BillingFooter,
    ReviewAction: ReviewInExtension,
    Notice: InstallBanner,
    AfterOnboarding,
    // No background worker here: the extension picks settings up through the account.
    notifyBackground: () => {},
    // A second meaning of a word becomes its own card only when the learner's extension shows
    // such cards in reviews (an older one would not); the simulated sign-in has no extension.
    sensesEnabled: async () =>
      isDevAuthMock() || versionAtLeast(await extensionVersion(), SENSES_SINCE),
    paddle: PADDLE_CONFIG,
  });
  getSyncEngine().onSent(() => void tellExtension());
  clearDataWhenSignedOut(getSupabase().auth, {
    clear: () => clearLocalData(store),
    reload: () => window.location.reload(),
    isSimulatedSignIn: isDevAuthMock,
  });
}
